import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    BarChart, Bar, ResponsiveContainer,
} from 'recharts';
import { analyticsAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import { guestIdentity, guestDetails } from '../../utils/guest';
import { downloadXlsx } from '../../utils/excel';
import './Traffic.css';

const GB = 1073741824;
const MB = 1048576;
const KB = 1024;
const DAY_MS = 86400e3;

function fmtBytes(b) {
    if (!b || b === 0) return '0 B';
    if (b >= GB) return `${(b / GB).toFixed(2)} GB`;
    if (b >= MB) return `${(b / MB).toFixed(1)} MB`;
    if (b >= KB) return `${(b / KB).toFixed(0)} KB`;
    return `${b} B`;
}
function fmtBytesShort(b) {
    if (!b || b === 0) return '0';
    if (b >= GB) return `${(b / GB).toFixed(1)}G`;
    if (b >= MB) return `${(b / MB).toFixed(0)}M`;
    if (b >= KB) return `${(b / KB).toFixed(0)}K`;
    return `${b}`;
}

// Rates are bits per second, shown in the unit the figure (or the graph's peak) calls for.
const RATE_UNITS = [[1e9, 'Gbps'], [1e6, 'Mbps'], [1e3, 'kbps'], [1, 'bps']];
const rateUnit = (bps) => RATE_UNITS.find(([size]) => bps >= size) || RATE_UNITS[RATE_UNITS.length - 1];
function fmtRate(bps, unit = rateUnit(bps)) {
    const v = bps / unit[0];
    return `${v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)} ${unit[1]}`;
}

// The graph is an average over each slot of the period, as MRTG / Cacti draw it.
const PERIODS = [
    { key: '24h', label: '24 hours', days: 1,   avg: '30-minute average' },
    { key: '7d',  label: '7 days',   days: 7,   avg: '2-hour average' },
    { key: '30d', label: '30 days',  days: 30,  avg: '1-day average' },
    { key: '6m',  label: '6 months', days: 183, avg: '1-day average' },
    { key: '1y',  label: '1 year',   days: 371, avg: '1-week average' },
];

// One colour per gateway, in a fixed order (the validated categorical palette).
const IFACE_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];

const day = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const clock = (d) => d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
const tickFormat = (period) => (iso) => {
    const d = new Date(iso);
    if (period === '24h') return clock(d);
    if (period === '7d') return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric' });
    return day(d);
};
function slotLabel(t, slotSeconds) {
    const a = new Date(t);
    const b = new Date(a.getTime() + slotSeconds * 1000);
    if (slotSeconds < 86400) return `${day(a)}, ${clock(a)} – ${clock(b)}`;
    if (slotSeconds === 86400) return a.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    return `Week of ${day(a)} ${a.getFullYear()}`;
}

function RateTooltip({ active, payload, slotSeconds, unit }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="traffic-tooltip">
            <p className="traffic-tooltip-label">{slotLabel(p.t, slotSeconds)}{p.partial ? ' (so far)' : ''}</p>
            <p className="tg-tip-row"><span className="tg-swatch tg-swatch-down"></span>Download <strong>{fmtRate(p.down, unit)}</strong> · {fmtBytes(p.downBytes)}</p>
            <p className="tg-tip-row"><span className="tg-swatch tg-swatch-up"></span>Upload <strong>{fmtRate(p.up, unit)}</strong> · {fmtBytes(p.upBytes)}</p>
        </div>
    );
}

function BytesTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="traffic-tooltip" style={{ minWidth: 140 }}>
            <p className="traffic-tooltip-label">{label}</p>
            {payload.map((p) => (
                <p key={p.dataKey} className="tg-tip-row">
                    <span className={`tg-swatch tg-swatch-${p.dataKey === 'download' ? 'down' : 'up'}`}></span>
                    {p.name} <strong>{fmtBytes(p.value)}</strong>
                </p>
            ))}
        </div>
    );
}

/**
 * Interface Traffic — the average traffic on the gateways over 24 hours, 7 days, 30 days,
 * 6 months or a year (GET /api/analytics/traffic-graph: what moved in each slot, from the
 * RADIUS accounting), and each gateway's totals, top users and sessions over the period
 * (GET /api/analytics/interface-traffic). Not live: the routers are not asked.
 */
const Traffic = () => {
    const { selectedBranch } = useBranchStore();
    const [period, setPeriod] = useState(PERIODS[0]);
    const [selectedIface, setSelectedIface] = useState(''); // '' = every gateway
    const [graph, setGraph] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showTable, setShowTable] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const now = new Date();
            const from = new Date(now.getTime() - period.days * DAY_MS);
            const [g, t] = await Promise.all([
                analyticsAPI.getTrafficGraph({ period: period.key, branchId: selectedBranch?._id, gateway: selectedIface || undefined }),
                analyticsAPI.getInterfaceTraffic({ branchId: selectedBranch?._id, from: from.toISOString(), to: now.toISOString(), groupBy: 'day' }),
            ]);
            setGraph(g.data.data);
            setData(t.data.data);
        } catch {
            toast.error('Failed to fetch traffic data');
        } finally {
            setLoading(false);
        }
    }, [selectedBranch, period, selectedIface]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── The graph: bytes in each slot → its average rate in bits per second ──
    const slotSeconds = graph?.slotSeconds || 1800;
    const series = useMemo(() => (graph?.points || []).map((p) => ({
        t: p.t,
        partial: !!p.partial,
        down: (p.download * 8) / p.seconds,
        up: (p.upload * 8) / p.seconds,
        downBytes: p.download,
        upBytes: p.upload,
        seconds: p.seconds,
    })), [graph]);

    const stats = useMemo(() => {
        const seconds = series.reduce((n, s) => n + s.seconds, 0) || 1;
        const totalDown = series.reduce((n, s) => n + s.downBytes, 0);
        const totalUp = series.reduce((n, s) => n + s.upBytes, 0);
        // The peak is a whole slot's — the one still running is not yet one.
        const complete = series.filter((s) => !s.partial);
        const peakOf = (k) => complete.reduce((best, s) => (s[k] > (best ? best[k] : 0) ? s : best), null);
        return { avgDown: (totalDown * 8) / seconds, avgUp: (totalUp * 8) / seconds, totalDown, totalUp, peakDown: peakOf('down'), peakUp: peakOf('up') };
    }, [series]);
    const unit = rateUnit(Math.max(1, ...series.map((s) => Math.max(s.down, s.up))));
    const hasTraffic = stats.totalDown + stats.totalUp > 0;

    // ── Per gateway, over the same period ──
    const allInterfaces = data?.byInterface || [];
    const interfaces = selectedIface ? allInterfaces.filter((f) => f.interface === selectedIface) : allInterfaces;
    const topUsers = (data?.topUsers || []).filter((u) => !selectedIface || u.iface === selectedIface);
    const sessionTotals = selectedIface
        ? { sessions: interfaces[0]?.sessions || 0, uniqueUsers: interfaces[0]?.uniqueUsers || 0 }
        : { sessions: data?.summary?.sessions || 0, uniqueUsers: data?.summary?.uniqueUsers || 0 };
    const ifaceColor = (key) => IFACE_COLORS[Math.max(0, allInterfaces.findIndex((f) => f.interface === key)) % IFACE_COLORS.length];
    const selectedName = allInterfaces.find((f) => f.interface === selectedIface)?.nasName || selectedIface;

    // When the peak slot began (its whole range is in the graph's tooltip).
    const peakSub = (s) => {
        if (!s) return 'No traffic yet';
        const a = new Date(s.t);
        return slotSeconds < 86400 ? `${day(a)}, ${clock(a)}` : slotLabel(s.t, slotSeconds);
    };
    // What the page shows, as an Excel file — a sheet each for the traffic over time,
    // the top users and the gateways; sizes in MB and rates in Mbps, as numbers.
    const toMB = (bytes) => Math.round(((bytes || 0) / MB) * 100) / 100;
    const toMbps = (bps) => Math.round(((bps || 0) / 1e6) * 1000) / 1000;
    const handleExport = () => {
        const grand = allInterfaces.reduce((n, f) => n + (f.total || 0), 0);
        downloadXlsx(`interface-traffic-${period.key}-${new Date().toISOString().slice(0, 10)}`, {
            sheets: [
                {
                    name: 'Traffic over time',
                    columns: [
                        { label: 'Time', value: (s) => new Date(s.t) },
                        { label: 'Download average (Mbps)', value: (s) => toMbps(s.down) },
                        { label: 'Upload average (Mbps)', value: (s) => toMbps(s.up) },
                        { label: 'Downloaded (MB)', value: (s) => toMB(s.downBytes) },
                        { label: 'Uploaded (MB)', value: (s) => toMB(s.upBytes) },
                        { label: 'Slot still running', value: (s) => (s.partial ? 'Yes' : '') },
                    ],
                    rows: series,
                },
                {
                    name: 'Top users',
                    serial: false, // ranked already
                    columns: [
                        { label: 'Rank', value: (u) => topUsers.indexOf(u) + 1 },
                        { label: 'Guest', value: (u) => guestIdentity(u)?.label || 'Guest' },
                        { label: 'Details', value: (u) => guestDetails(u).join(' · ') },
                        { label: 'Gateway', field: 'iface' },
                        { label: 'Download (MB)', value: (u) => toMB(u.download) },
                        { label: 'Upload (MB)', value: (u) => toMB(u.upload) },
                        { label: 'Total (MB)', value: (u) => toMB(u.total) },
                    ],
                    rows: topUsers,
                },
                {
                    name: 'Gateways',
                    columns: [
                        { label: 'Gateway', value: (f) => f.nasName || f.interface },
                        { label: 'Interface', field: 'interface' },
                        { label: 'Type', value: (f) => f.nasType || 'other' },
                        { label: 'IP Address', field: 'ipAddress' },
                        { label: 'Status', value: (f) => (f.hasTraffic ? 'Traffic' : 'No traffic') },
                        { label: 'Download (MB)', value: (f) => toMB(f.download) },
                        { label: 'Upload (MB)', value: (f) => toMB(f.upload) },
                        { label: 'Total (MB)', value: (f) => toMB(f.total) },
                        { label: 'Sessions', value: (f) => f.sessions || 0 },
                        { label: '% of total', value: (f) => (grand ? Math.round(((f.total || 0) / grand) * 1000) / 10 : 0) },
                    ],
                    rows: interfaces,
                },
            ],
        });
    };

    const tiles = [
        { label: 'Average download', value: fmtRate(stats.avgDown), sub: `over ${period.label}`, icon: 'fa-arrow-down', tone: 'down' },
        { label: 'Average upload', value: fmtRate(stats.avgUp), sub: `over ${period.label}`, icon: 'fa-arrow-up', tone: 'up' },
        { label: 'Peak download', value: stats.peakDown ? fmtRate(stats.peakDown.down) : '—', sub: peakSub(stats.peakDown), icon: 'fa-gauge-high', tone: 'down' },
        { label: 'Peak upload', value: stats.peakUp ? fmtRate(stats.peakUp.up) : '—', sub: peakSub(stats.peakUp), icon: 'fa-gauge-high', tone: 'up' },
        { label: 'Data used', value: fmtBytes(stats.totalDown + stats.totalUp), sub: `↓ ${fmtBytes(stats.totalDown)} · ↑ ${fmtBytes(stats.totalUp)}`, icon: 'fa-database', tone: 'navy' },
        { label: 'Sessions', value: sessionTotals.sessions.toLocaleString(), sub: `${sessionTotals.uniqueUsers.toLocaleString()} unique users`, icon: 'fa-wifi', tone: 'navy' },
    ];

    return (
        <div className="page-container-modern traffic-viz">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-chart-area"></i> Interface Traffic
                        </h1>
                        <p className="page-subtitle-modern">
                            {selectedIface
                                ? <>Showing <strong>{selectedName}</strong> — <button type="button" className="tg-link" onClick={() => setSelectedIface('')}>show every gateway</button></>
                                : 'Average download / upload on the gateways, from their RADIUS accounting'}
                        </p>
                    </div>
                    <div className="header-actions">
                        <div className="iface-select-wrap">
                            <i className="fas fa-server iface-select-icon"></i>
                            <select className="iface-select" aria-label="Gateway" value={selectedIface} onChange={(e) => setSelectedIface(e.target.value)}>
                                <option value="">All gateways ({allInterfaces.length})</option>
                                {allInterfaces.map((iface) => (
                                    <option key={iface.interface} value={iface.interface}>
                                        {iface.nasName && iface.nasName !== iface.interface ? `${iface.nasName} (${iface.interface})` : iface.interface}
                                        {iface.hasTraffic ? ` — ${fmtBytes(iface.total)}` : ' — No traffic'}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="period-pills" role="group" aria-label="Period">
                            {PERIODS.map((p) => (
                                <button key={p.key} type="button" aria-pressed={period.key === p.key}
                                    className={`period-pill ${period.key === p.key ? 'active' : ''}`} onClick={() => setPeriod(p)}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button className="btn-secondary-modern" onClick={handleExport} disabled={loading || !graph}>
                            <i className="fas fa-file-excel"></i> Excel
                        </button>
                        <button className="btn-secondary-modern" onClick={fetchData} disabled={loading}>
                            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i> Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="traffic-summary-grid">
                {tiles.map((c) => (
                    <div key={c.label} className="traffic-card">
                        <div className={`traffic-card-icon tg-tone-${c.tone}`}><i className={`fas ${c.icon}`}></i></div>
                        <div className="tg-card-body">
                            <p className="traffic-card-label">{c.label}</p>
                            <p className="traffic-card-value">{loading && !graph ? '—' : c.value}</p>
                            <small className="tg-card-sub">{c.sub}</small>
                        </div>
                    </div>
                ))}
            </div>

            {loading && !graph ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading traffic…</p></div>
            ) : (
                <>
                    {/* ── Average traffic over the period ── */}
                    <div className="traffic-chart-card">
                        <div className="traffic-chart-header">
                            <h3 className="traffic-section-title" style={{ margin: 0 }}>
                                <i className="fas fa-chart-area"></i> Average traffic — last {period.label}
                                <span className="tg-avg-note">{period.avg}</span>
                            </h3>
                            <button type="button" className={`mode-pill tg-table-toggle ${showTable ? 'active' : ''}`} aria-pressed={showTable} onClick={() => setShowTable((v) => !v)}>
                                <i className="fas fa-table"></i> Table
                            </button>
                        </div>
                        <ul className="tg-legend" aria-label="Series">
                            <li><span className="tg-swatch tg-swatch-down"></span>Download <small>to the guests</small> <strong>avg {fmtRate(stats.avgDown, unit)}</strong></li>
                            <li><span className="tg-swatch tg-swatch-up tg-swatch-line"></span>Upload <small>from the guests</small> <strong>avg {fmtRate(stats.avgUp, unit)}</strong></li>
                        </ul>

                        {!hasTraffic ? (
                            <div className="traffic-empty">No traffic recorded in the last {period.label}</div>
                        ) : showTable ? (
                            <div className="tg-table-wrap">
                                <table className="table-modern">
                                    <thead>
                                        <tr><th>Time</th><th>Download (average)</th><th>Upload (average)</th><th>Downloaded</th><th>Uploaded</th></tr>
                                    </thead>
                                    <tbody>
                                        {[...series].reverse().map((s) => (
                                            <tr key={s.t}>
                                                <td>{slotLabel(s.t, slotSeconds)}{s.partial ? ' (so far)' : ''}</td>
                                                <td>{fmtRate(s.down, unit)}</td>
                                                <td>{fmtRate(s.up, unit)}</td>
                                                <td>{fmtBytes(s.downBytes)}</td>
                                                <td>{fmtBytes(s.upBytes)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <ComposedChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                    <CartesianGrid stroke="var(--tg-grid)" vertical={false} />
                                    <XAxis dataKey="t" tickFormatter={tickFormat(period.key)} minTickGap={32}
                                        tick={{ fontSize: 11, fill: 'var(--tg-axis)' }} tickLine={false} axisLine={{ stroke: 'var(--tg-baseline)' }} />
                                    <YAxis tickFormatter={(v) => { const x = v / unit[0]; return x >= 10 ? x.toFixed(0) : x.toFixed(1); }}
                                        tick={{ fontSize: 11, fill: 'var(--tg-axis)' }} tickLine={false} axisLine={false} width={44}
                                        label={{ value: unit[1], angle: -90, position: 'insideLeft', offset: 12, fill: 'var(--tg-axis)', fontSize: 11 }} />
                                    <Tooltip content={<RateTooltip slotSeconds={slotSeconds} unit={unit} />} cursor={{ stroke: 'var(--tg-baseline)', strokeWidth: 1 }} />
                                    <Area type="monotone" dataKey="down" name="Download" stroke="var(--tg-down)" fill="var(--tg-down)" fillOpacity={0.14}
                                        strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--tg-surface)' }} isAnimationActive={false} />
                                    <Line type="monotone" dataKey="up" name="Upload" stroke="var(--tg-up)" strokeWidth={2}
                                        dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--tg-surface)' }} isAnimationActive={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* ── Per gateway: totals + top users ── */}
                    <div className="traffic-two-col">
                        <div className="traffic-chart-card">
                            <h3 className="traffic-section-title"><i className="fas fa-server"></i> Gateway totals</h3>
                            {interfaces.length === 0 ? (
                                <div className="traffic-empty">No gateway data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={Math.max(200, interfaces.length * 60)}>
                                    <BarChart data={interfaces} layout="vertical" margin={{ left: 8, right: 16, top: 4 }} barGap={2}>
                                        <CartesianGrid stroke="var(--tg-grid)" horizontal={false} />
                                        <XAxis type="number" tickFormatter={fmtBytesShort} tick={{ fontSize: 10, fill: 'var(--tg-axis)' }} />
                                        <YAxis type="category" dataKey="nasName" tick={{ fontSize: 10, fill: 'var(--tg-axis)' }} width={130} />
                                        <Tooltip content={<BytesTooltip />} />
                                        <Bar dataKey="download" name="Download" fill="var(--tg-down)" radius={[0, 4, 4, 0]} />
                                        <Bar dataKey="upload" name="Upload" fill="var(--tg-up)" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className="traffic-chart-card">
                            <h3 className="traffic-section-title"><i className="fas fa-trophy"></i> Top data users</h3>
                            {topUsers.length === 0 ? (
                                <div className="traffic-empty">No user data</div>
                            ) : (
                                <div className="top-consumers-list">
                                    {topUsers.map((u, i) => {
                                        const details = [...guestDetails(u), u.iface].filter(Boolean).join(' · ');
                                        return (
                                            <div key={`${u._id || i}`} className="consumer-row">
                                                <span className="consumer-rank">#{i + 1}</span>
                                                <div className="consumer-info">
                                                    <strong>{guestIdentity(u)?.label || 'Guest'}</strong>
                                                    <span>{details || '—'}</span>
                                                </div>
                                                <div className="consumer-stats">
                                                    <span className="stat-down"><i className="fas fa-arrow-down"></i> {fmtBytes(u.download)}</span>
                                                    <span className="stat-up"><i className="fas fa-arrow-up"></i> {fmtBytes(u.upload)}</span>
                                                    <span className="stat-total">{fmtBytes(u.total)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Gateway breakdown ── */}
                    <div className="traffic-chart-card">
                        <h3 className="traffic-section-title"><i className="fas fa-table"></i> Gateway breakdown — last {period.label}</h3>
                        {interfaces.length === 0 ? (
                            <div className="traffic-empty">No gateways registered</div>
                        ) : (
                            <div className="table-wrapper-modern">
                                <table className="table-modern">
                                    <thead>
                                        <tr>
                                            <th>Gateway</th><th>Type</th><th>IP Address</th><th>Status</th>
                                            <th>Download</th><th>Upload</th><th>Total</th><th>Sessions</th><th>% of total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {interfaces.map((iface) => {
                                            const grand = allInterfaces.reduce((n, f) => n + (f.total || 0), 0);
                                            const pct = grand ? ((iface.total / grand) * 100).toFixed(1) : 0;
                                            return (
                                                <tr key={iface.interface} className={!iface.hasTraffic ? 'iface-row-idle' : ''} style={{ cursor: 'pointer' }}
                                                    onClick={() => setSelectedIface((prev) => (prev === iface.interface ? '' : iface.interface))}
                                                    title={selectedIface === iface.interface ? 'Show every gateway' : `Show only ${iface.nasName || iface.interface}`}>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <span className="iface-dot" style={{ background: iface.hasTraffic ? ifaceColor(iface.interface) : '#d1d5db' }}></span>
                                                            <div>
                                                                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{iface.nasName || iface.interface}</div>
                                                                {iface.nasName && iface.nasName !== iface.interface && <code style={{ fontSize: '0.72rem', color: '#64748b' }}>{iface.interface}</code>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="nas-type-badge">
                                                            <i className={`fas ${iface.nasType === 'mikrotik' ? 'fa-network-wired' : 'fa-server'}`}></i>
                                                            {iface.nasType || 'other'}
                                                        </span>
                                                    </td>
                                                    <td><code style={{ fontSize: '0.78rem' }}>{iface.ipAddress || '—'}</code></td>
                                                    <td>
                                                        <span className={`iface-status-badge ${iface.hasTraffic ? 'active' : 'idle'}`}>
                                                            <span className="badge-dot"></span> {iface.hasTraffic ? 'Traffic' : 'No traffic'}
                                                        </span>
                                                    </td>
                                                    <td><span className="stat-down"><i className="fas fa-arrow-down"></i> {fmtBytes(iface.download)}</span></td>
                                                    <td><span className="stat-up"><i className="fas fa-arrow-up"></i> {fmtBytes(iface.upload)}</span></td>
                                                    <td><strong>{fmtBytes(iface.total)}</strong></td>
                                                    <td>{(iface.sessions || 0).toLocaleString()}</td>
                                                    <td>
                                                        <div className="pct-bar-wrap">
                                                            <div className="pct-bar" style={{ width: `${Math.min(80, pct)}px` }}></div>
                                                            <span>{pct}%</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Traffic;
